const LOCALE_PREFIX = /^\/[a-z]{2}(-[a-z]{2,4})?(?=\/|$)/i;

export function safeRedirectPath(path: string | undefined | null): string | undefined {
  if (!path) return undefined;
  if (!path.startsWith("/")) return undefined;
  if (path.startsWith("//") || path.startsWith("/\\")) return undefined;
  if (path.startsWith("/api/")) return undefined;
  const pathWithoutLocale = path.replace(LOCALE_PREFIX, "") || "/";
  if (pathWithoutLocale === "/login" || pathWithoutLocale.startsWith("/login?") || pathWithoutLocale.startsWith("/login/")) return undefined;
  if (pathWithoutLocale === "/logout" || pathWithoutLocale.startsWith("/logout?")) return undefined;
  return path;
}

type RouteQueryLike = Record<string, string | (string | null)[] | null | undefined>;

export function extractSafeRedirect(query: RouteQueryLike | undefined | null): string | undefined {
  const value = query?.redirect;
  const raw = Array.isArray(value) ? value[0] : value;
  return safeRedirectPath(typeof raw === "string" ? raw : undefined);
}

// Marqueur ajouté à la query lorsque l'app force un logout (session non récupérable).
// Permet à /login d'afficher un toast distinct du simple « visite sans session ».
export const LOGOUT_REASON_QUERY_KEY = "reason";
export const LOGOUT_REASON_SESSION_EXPIRED = "session-expired";

export function isAuthenticatedRoute(route: { meta?: { middleware?: unknown } } | undefined | null): boolean {
  const middleware = route?.meta?.middleware;
  return Array.isArray(middleware) ? middleware.includes("authenticated") : middleware === "authenticated";
}

const OAUTH2_AUTH_FAILURE_CODES = new Set(["invalid_grant", "invalid_token", "invalid_client", "unauthorized_client"]);

export function isOAuth2AuthFailure(error: unknown): boolean {
  const err = error as { statusCode?: number; status?: number; statusMessage?: string; data?: { statusMessage?: string; oauth2Code?: string } };
  const statusCode = err?.statusCode ?? err?.status;
  if (statusCode && statusCode >= 400 && statusCode < 500) return true;
  const oauth2Code = err?.statusMessage ?? err?.data?.statusMessage ?? err?.data?.oauth2Code;
  return !!oauth2Code && OAUTH2_AUTH_FAILURE_CODES.has(oauth2Code);
}

// Worth preserving the session through:
// - 5xx server errors without an OAuth2 auth-failure indicator (SSO temporarily down)
// - Errors without statusCode (network failures, timeouts, aborts — flaky connection
//   shouldn't force a logout; the next authenticated call will retry the refresh)
// 4xx failures and OAuth2 auth-failure codes return false → trigger logout.
export function isTransientRefreshError(error: unknown): boolean {
  const err = error as { statusCode?: number; status?: number };
  const statusCode = err?.statusCode ?? err?.status;
  if (!statusCode) return true;
  if (statusCode < 500 || statusCode >= 600) return false;
  return !isOAuth2AuthFailure(error);
}
